<script lang="ts">
  import Avatar from "$components/+shared/Avatar/Avatar.svelte";
  import Link from "$components/+design-system/Dropdown/fields/Link.svelte";
  import Action from "$components/+design-system/Dropdown/fields/Action.svelte";
  import SidebarNavItem from "./SidebarNavItem.svelte";
  import IconButton from "$components/+design-system/IconButton/IconButton.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import Separator from "$components/+shared/Separator/Separator.svelte";
  import {
    isChangelogEnabled,
    communityFlag,
    isCommunityEnabled,
    isDocumentsEnabled,
    sandboxFlag,
    changelogFlag,
    isAskAiTeamOnly,
    knowledgeBaseUIFlag,
    zendeskFlag,
    community,
    analyticsFlag,
  } from "app/stores/community";
  import { Theme } from "app/models/shared-types";
  import { getStores } from "$stores";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import { auth, session, userHasOutverseEmail } from "app/stores/auth";
  import {
    Book,
    Cog,
    Cover,
    Insights,
    KB,
    LA,
    Moon,
    Outverse,
    ReplyFilledCheck,
    Sun,
  } from "$components/+shared/OVIcon";
  import Zendesk from "$assets/images/logos/Zendesk.svg?url";
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import OutverseAgent from "$assets/images/logos/OutverseAgent.svg?url";
  import WorkspaceSwitcher from "app/components/community/WorkspaceSwitcher.svelte";
  import {
    changelogRootURL,
    communityRootUrl,
    communitySettingsURL,
    contextSourcesURL,
    docRootURL,
    userProfileUrl,
    zendeskRootUrl,
    agentRootURL,
    insightsUrl,
  } from "app/utils/urls";
  import { tooltip } from "app/actions/tooltip";
  import {
    canEditCommunitySettings,
    canViewContextSources,
    isTeamMember,
  } from "app/stores/permissions";
  import { fade, slide } from "svelte/transition";
  import { clickOutside } from "app/actions/clickOutside";
  import { portal } from "app/actions/portal.svelte";
  import { createPopperActions } from "svelte-popperjs";

  export let communitySlug: string;

  const { userPreferences } = getStores();
  const isDesktop = getMediaQuery("desktop");

  let isWorkspaceSwitcherOpen = false;

  const [popperRef, popperContent] = createPopperActions({
    placement: "bottom-start",
    strategy: "fixed",
  });
</script>

<!-- a11y: id given to nav so screen readers can differentiate between multiple nav sections (ie when this sidebar is shown as well as the global top bar) -->
<nav
  class="flex h-dvh animate-fade-in-left flex-col items-center justify-between bg-neutral-100 p-2 dark:bg-neutral-950"
  id="admin-sidebar-nav"
  in:slide={{ axis: "x", duration: 100 }}
>
  <div class="mt-[18px] flex flex-col items-center gap-5">
    <button
      class="flex size-[36px] items-center justify-center overflow-hidden rounded-lg"
      use:tooltip
      title={$community?.name}
      on:click={() => (isWorkspaceSwitcherOpen = true)}
      use:popperRef
    >
      <OVIcon icon={Outverse} size="size-5" class="text-neutral-200 dark:text-neutral-700" />
    </button>

    {#if isWorkspaceSwitcherOpen && $userHasOutverseEmail}
      <div
        use:clickOutside={() => {
          isWorkspaceSwitcherOpen = false;
          return true;
        }}
        use:portal={{ target: "body", hasBackLayer: true }}
        use:popperContent
      >
        <WorkspaceSwitcher bind:isWorkspaceSwitcherOpen />
      </div>
    {/if}

    <!-- TODO: THIS WILL BE REINTRAGRATED AT A LATER DATE -->
    <!-- {#if $fullPagePrototypeFlag}
      <SidebarNavItem
        icon={Blur}
        title="Full page prototype"
        routeId="/[community=slug]/beta-sandbox"
        path={fullPagePrototypeURL({ communitySlug })}
      />
    {/if} -->

    {#if $sandboxFlag && (!$isAskAiTeamOnly || $isTeamMember)}
      <SidebarNavItem
        image={OutverseAgent}
        title="Outverse agent"
        routeId="/[community=slug]/outverse-agent"
        path={agentRootURL({ communitySlug })}
      />
    {/if}

    {#if $zendeskFlag}
      <SidebarNavItem
        image={Zendesk}
        title="Zendesk agent"
        routeId="/[community=slug]/zendesk"
        path={zendeskRootUrl({ communitySlug })}
      />
    {/if}

    {#if $sandboxFlag && $canViewContextSources}
      <Separator class="w-full" />

      <SidebarNavItem
        icon={Book}
        title="Context sources"
        routeId="/[community=slug]/context-sources"
        path={contextSourcesURL({
          communitySlug,
        })}
      />
    {/if}

    {#if $knowledgeBaseUIFlag}
      <SidebarNavItem
        icon={KB}
        title={$isDocumentsEnabled ? "Help center" : "Enable help center in workspace settings"}
        routeId="/[community=slug]/(knowledge-base)"
        path={docRootURL({ communitySlug })}
        disabled={!$isDocumentsEnabled}
      />
    {/if}
    {#if $analyticsFlag}
      <SidebarNavItem
        icon={Insights}
        title="Analytics"
        routeId="/[community=slug]/insights"
        path={insightsUrl({ communitySlug })}
      />
    {/if}
    {#if $communityFlag}
      <SidebarNavItem
        icon={ReplyFilledCheck}
        title={$isCommunityEnabled ? "Forums" : "Enable forums in workspace settings"}
        routeId="/[community=slug]/forums"
        path={communityRootUrl({
          communitySlug,
        })}
        disabled={!$isCommunityEnabled}
      />
    {/if}
    {#if $changelogFlag}
      <SidebarNavItem
        icon={Cover}
        title={$isChangelogEnabled ? "Changelog" : "Enable changelog in workspace settings"}
        routeId="/[community=slug]/changelog"
        path={changelogRootURL({
          communitySlug,
        })}
        disabled={!$isChangelogEnabled}
      />
    {/if}
    {#if $canEditCommunitySettings}
      <SidebarNavItem
        icon={Cog}
        title="Workspace settings"
        routeId="/[community=slug]/settings"
        path={communitySettingsURL({
          communitySlug,
          section: "general",
          subSection: "overview",
        })}
      />
    {/if}
  </div>

  <div in:fade={{ duration: 100 }} class="flex flex-col items-center gap-6 py-2">
    <IconButton
      variant="tertiary"
      icon={$userPreferences.theme === Theme.Dark ? Moon : Sun}
      a11yLabel="Switch theme"
      customIconSize="size-[18px]"
      on:click={() => userPreferences.toggleTheme()}
    />

    {#if $isDesktop && $auth.isAuthed}
      <Dropdown>
        <div slot="trigger" aria-label="user-menu">
          <Avatar
            mainImage={$session?.user?.profileImage}
            label={$session?.user?.handle?.charAt(0)}
            size="size-[28px]"
          />
        </div>

        <Link href={userProfileUrl()} title="User settings" icon={Cog} />
        <Action danger title="Log out" icon={LA} on:click={() => session.signout()} />
      </Dropdown>
    {/if}
  </div>
</nav>
